import React, { useState } from 'react';
import '../styles/FAQ.css';

const FAQ = () => {
    const [openItem, setOpenItem] = useState(null);

    const toggleItem = (index) => {
        setOpenItem(openItem === index ? null : index);
    };

    const faqCategories = [
        {
            title: 'Product Questions',
            icon: '🛢️',
            faqs: [
                {
                    question: 'How do I choose the right engine oil for my vehicle?',
                    answer: 'To choose the right engine oil, consider your vehicle\'s make and model, driving conditions, climate, and manufacturer recommendations. Check your owner\'s manual for the recommended viscosity grade and API rating. MapLube offers a range of oils suitable for different vehicles and conditions.'
                },
                {
                    question: 'What is the difference between synthetic and mineral oils?',
                    answer: 'Synthetic oils are chemically engineered for superior performance, offering better protection, longer life, and improved fuel efficiency. Mineral oils are refined from crude oil and are more affordable but require more frequent changes. MapLube offers both options to meet different needs and budgets.'
                },
                {
                    question: 'How often should I change my engine oil?',
                    answer: 'Oil change intervals depend on your vehicle, driving conditions, and oil type. Generally, conventional oil should be changed every 3,000-5,000 miles, while synthetic oils can last 7,500-15,000 miles. Check your owner\'s manual for specific recommendations.'
                },
                {
                    question: 'Are MapLube products compatible with all vehicles?',
                    answer: 'MapLube products are designed to meet or exceed industry standards and are compatible with most vehicles. However, we recommend checking your vehicle\'s owner\'s manual for specific requirements and consulting with our technical team for specialized applications.'
                }
            ]
        },
        {
            title: 'Quality & Standards',
            icon: '🏆',
            faqs: [
                {
                    question: 'What quality certifications does MapLube have?',
                    answer: 'MapLube holds multiple quality certifications including ISO 9001:2015 for quality management, API certifications for engine oil performance standards, and BIS certification for Indian standards compliance. We maintain rigorous quality control throughout our manufacturing process.'
                },
                {
                    question: 'How does MapLube ensure product quality?',
                    answer: 'We maintain quality through strict testing protocols, advanced manufacturing processes, and continuous monitoring. Every batch undergoes comprehensive testing for viscosity, performance, and stability before approval. Our state-of-the-art laboratory ensures consistent quality.'
                },
                {
                    question: 'Do MapLube products meet international standards?',
                    answer: 'Yes, MapLube products meet and often exceed international standards including API, ACEA, and JASO specifications. Our formulations are designed to provide optimal performance and protection under various operating conditions.'
                },
                {
                    question: 'What testing is performed on MapLube products?',
                    answer: 'Our products undergo extensive testing including viscosity measurement, flash point testing, oxidation stability, wear protection, and environmental impact assessments. We also perform engine testing to validate performance claims.'
                }
            ]
        },
        {
            title: 'Dealership & Distribution',
            icon: '🤝',
            faqs: [
                {
                    question: 'How can I become a MapLube dealer?',
                    answer: 'To become a MapLube dealer, you need business experience in automotive or lubricant industry, adequate financial resources, strategic location, and commitment to our brand values. Contact our dealership team for detailed requirements and application process.'
                },
                {
                    question: 'What support do MapLube dealers receive?',
                    answer: 'MapLube dealers receive comprehensive support including marketing materials, technical training, business development guidance, and logistics support. We provide ongoing assistance to help dealers grow their business and serve customers effectively.'
                },
                {
                    question: 'Where can I buy MapLube products?',
                    answer: 'MapLube products are available through our authorized dealer network across India. You can find our products at automotive stores, service centers, and authorized distributors. Contact us to find the nearest dealer in your area.'
                },
                {
                    question: 'Do you offer bulk purchasing options?',
                    answer: 'Yes, we offer bulk purchasing options for commercial customers, fleet operators, and large-scale users. Contact our sales team for competitive pricing and delivery arrangements for bulk orders.'
                }
            ]
        },
        {
            title: 'Technical Support',
            icon: '🔧',
            faqs: [
                {
                    question: 'How can I get technical support for MapLube products?',
                    answer: 'Our technical support team is available through multiple channels including phone, email, and online chat. We provide expert guidance on product selection, application, and troubleshooting. Contact our technical team for personalized assistance.'
                },
                {
                    question: 'Do you provide training for product usage?',
                    answer: 'Yes, we offer comprehensive training programs for dealers, service centers, and customers. Our training covers product knowledge, proper application techniques, and best practices for optimal performance and safety.'
                },
                {
                    question: 'What should I do if I have a product-related issue?',
                    answer: 'If you experience any product-related issues, contact our customer support team immediately. We will investigate the issue and provide appropriate solutions. Your satisfaction and safety are our top priorities.'
                },
                {
                    question: 'Can MapLube help with equipment lubrication analysis?',
                    answer: 'Yes, our technical team can help with lubrication analysis and recommendations for industrial equipment. We provide customized solutions based on your specific application requirements and operating conditions.'
                }
            ]
        },
        {
            title: 'Warranty & Returns',
            icon: '🛡️',
            faqs: [
                {
                    question: 'What warranty does MapLube provide?',
                    answer: 'MapLube provides comprehensive warranty coverage for our products when used according to specifications. Warranty terms vary by product type and application. Contact us for specific warranty information for your use case.'
                },
                {
                    question: 'What is your return policy?',
                    answer: 'We accept returns of unopened products in original packaging within 30 days of purchase. Damaged or defective products are handled on a case-by-case basis. Contact our customer service for return authorization.'
                },
                {
                    question: 'How do I make a warranty claim?',
                    answer: 'To make a warranty claim, contact our customer service team with product details, purchase information, and description of the issue. We will guide you through the warranty claim process and provide appropriate resolution.'
                },
                {
                    question: 'Are MapLube products covered by insurance?',
                    answer: 'Our products are covered by product liability insurance. In case of any issues related to product quality or performance, we have appropriate insurance coverage to protect our customers and partners.'
                }
            ]
        }
    ];

    return (
        <div className="faq">
            {/* Hero Section */}
            <section className="faq-hero">
                <div className="container">
                    <div className="faq-hero-content">
                        <h1>Frequently Asked Questions</h1>
                        <p>Find answers to common questions about MapLube products, services, and support</p>
                    </div>
                </div>
            </section>

            {/* FAQ Categories */}
            <section className="faq-categories">
                <div className="container">
                    {faqCategories.map((category, categoryIndex) => (
                        <div key={categoryIndex} className="faq-category">
                            <div className="category-header">
                                <div className="category-icon">{category.icon}</div>
                                <h2>{category.title}</h2>
                            </div>
                            <div className="faq-list">
                                {category.faqs.map((faq, faqIndex) => {
                                    const itemIndex = categoryIndex * 100 + faqIndex;
                                    return (
                                        <div key={faqIndex} className="faq-item">
                                            <button
                                                className={`faq-question ${openItem === itemIndex ? 'active' : ''}`}
                                                onClick={() => toggleItem(itemIndex)}
                                            >
                                                <span>{faq.question}</span>
                                                <span className="faq-icon">
                                                    {openItem === itemIndex ? '−' : '+'}
                                                </span>
                                            </button>
                                            <div className={`faq-answer ${openItem === itemIndex ? 'open' : ''}`}>
                                                <p>{faq.answer}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Contact Support */}
            <section className="contact-support">
                <div className="container">
                    <div className="support-content">
                        <h2>Still Have Questions?</h2>
                        <p>Our support team is here to help you with any questions not covered in our FAQ</p>
                        <div className="support-options">
                            <div className="support-option">
                                <div className="support-icon">📞</div>
                                <h3>Call Us</h3>
                                <p>+91 9540 071804</p>
                                <span>Mon-Fri: 9:00 AM - 6:00 PM</span>
                            </div>
                            <div className="support-option">
                                <div className="support-icon">✉️</div>
                                <h3>Email Us</h3>
                                <p>support@maplube.in</p>
                                <span>We'll respond within 24 hours</span>
                            </div>
                            <div className="support-option">
                                <div className="support-icon">💬</div>
                                <h3>Live Chat</h3>
                                <p>Available on our website</p>
                                <span>Real-time support during business hours</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Quick Links */}
            <section className="quick-links">
                <div className="container">
                    <h2>Quick Links</h2>
                    <div className="links-grid">
                        <div className="link-card">
                            <h3>Product Catalog</h3>
                            <p>Browse our complete range of lubricants and oils</p>
                            <button className="btn btn-outline">View Products</button>
                        </div>
                        <div className="link-card">
                            <h3>Technical Data Sheets</h3>
                            <p>Download detailed specifications and technical information</p>
                            <button className="btn btn-outline">Download TDS</button>
                        </div>
                        <div className="link-card">
                            <h3>Dealership Application</h3>
                            <p>Apply to become a MapLube dealer or distributor</p>
                            <button className="btn btn-outline">Apply Now</button>
                        </div>
                        <div className="link-card">
                            <h3>Contact Sales</h3>
                            <p>Get in touch with our sales team for bulk orders</p>
                            <button className="btn btn-outline">Contact Sales</button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default FAQ; 